<template>
  <div class="pa-8">
    <template v-if="!isSubmitted">
      <div class="form-content">
        <StepProgressBar :label="currentStepLabel" :percent="currentStepPercent" :current-step="stepIndex + 1" :total-steps="steps.length" />

        <Transition :name="stepTransitionName" mode="out-in">
          <component 
            :is="currentStepComponent" 
            :key="currentStepKey" 
            v-bind="currentStepProps"
            v-on="currentStepListeners"
          >
            <template v-if="stepIndex === steps.length - 1" #payment>
              <PaymentBlock
                ref="paymentBlockRef"
                :publishable-key="stripeConfig.publishableKey || companyPublishableKey"
                :amount="membershipAmountDue"
              />
            </template>
          </component>
        </Transition>
      </div>

      <Teleport to="body">
        <div class="bottom-bar">
          <v-btn class="bottom-btn action-btn" variant="flat" color="gray-darken-1" size="large"
            prepend-icon="mdi-arrow-left" :disabled="stepIndex === 0" @click="handlePrevious">
            Previous
          </v-btn>

          <v-btn v-if="stepIndex < steps.length - 1" class="bottom-btn action-btn next-btn" color="primary"
            size="large" variant="flat" append-icon="mdi-arrow-right" :disabled="!canContinue" @click="handleNext">
            Next
          </v-btn>

          <v-btn v-else class="bottom-btn action-btn next-btn" color="primary" size="large" variant="flat"
            append-icon="mdi-arrow-right" :loading="isSubmitting" :disabled="isSubmitting" @click="handleSubmit">
            Submit
          </v-btn>
        </div>
      </Teleport>
    </template>

    <div v-else class="success-wrap">
      <v-icon size="56" color="success">mdi-check-circle-outline</v-icon>
      <h2 class="success-title">Payment Completed</h2>
      <p class="success-subtitle">
        Your vendor membership payment has been processed successfully. We will review your application and contact you soon.
      </p>
      <v-btn color="primary" variant="flat" @click="router.push({ name: 'login.page' })">
        Back to Login
      </v-btn>
    </div>
  </div>

</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import type { Component } from "vue";
import { useRouter } from "vue-router";
import { JOIN_AS_VENDOR_FORM_STEPS } from "@/data/form-steps";
import StepProgressBar from "@/shared/components/ui/flow/StepProgressBar.vue";
import BasicInfoForm from "@/modules/vendor/components/registration/BasicInfoForm.vue";
import ClassificationForm from "@/modules/vendor/components/registration/ClassificationForm.vue";
import MembershipPackageForm from "@/modules/vendor/components/registration/MembershipPackageForm.vue";
import ContactPersonForm from "@/modules/vendor/components/registration/ContactPersonForm.vue";
import AddressForm from "@/modules/vendor/components/registration/AddressForm.vue";
import DocumentsForm from "@/modules/vendor/components/registration/DocumentsForm.vue";
import SelfieForm from "@/modules/vendor/components/registration/SelfieForm.vue";
import ReviewForm from "@/modules/vendor/components/registration/ReviewForm.vue";
import PaymentBlock from "@/shared/components/ui/flow/PaymentBlock.vue";
import {
  createVendorRequest,
  type MembershipPackage,
  createVendorMembershipIntent,
  finalizeVendorMembership,
  chargeSavedVendorMembershipCard,
  fetchServiceGroups,
} from "@/shared/services/vendor-requests.api";

import TermsPrivacyPage from "@/shared/pages/TermsPrivacyPage.vue";
import { useModalStore } from "@/shared/stores/modal";
import { useAppStore } from "@/shared/stores/app";

interface JoinAsVendorFormState {
  membership: {
    package_id: string;
    billing_interval: "monthly" | "yearly";
  };
  basicInfo: {
    business_name: string;
    business_type: string;
    service_category: string;
    tax_id: string;
    business_email: string;
    business_phone: string;
  };
  contactPerson: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    phone_alt: string;
  };
  address: {
    line_1: string;
    line_2: string;
    city: string;
    state: string;
    zip: string;
    lat: string | null;
    lng: string | null;
    country_code: string | null;
    country: string | null;
  };
  documents: {
    id_front: File | null;
    id_back: File | null;
  };
  selfie: File | null;
  confirmations: {
    agree_terms: boolean;
    certify_information: boolean;
  };
}

const router = useRouter();
const appStore = useAppStore();
const steps = JOIN_AS_VENDOR_FORM_STEPS;
const stepIndex = ref(0);
const stepTransitionName = ref("step-forward");
const isSubmitting = ref(false);
const isSubmitted = ref(false);
const modal = useModalStore();
const stripeConfig = reactive<{ publishableKey: string; stripeAccount: string }>({
  publishableKey: "",
  stripeAccount: "",
});
const paymentBlockRef = ref<{
  createToken: () => Promise<{
    id: string;
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
  } | null>;
  confirmPayment: (
    clientSecret: string,
    billingDetails: Record<string, unknown>,
  ) => Promise<any>;
  confirmSetup: (
    clientSecret: string,
    billingDetails: Record<string, unknown>,
  ) => Promise<any>;
} | null>(null);

const form = reactive<JoinAsVendorFormState>({
  membership: {
    package_id: "",
    billing_interval: "monthly",
  },
  basicInfo: {
    business_name: "",
    business_type: "",
    service_category: "",
    tax_id: "",
    business_email: "",
    business_phone: "",
  },
  contactPerson: {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    phone_alt: "",
  },
  address: {
    line_1: "",
    line_2: "",
    city: "",
    state: "",
    zip: "",
    lat: null,
    lng: null,
    country_code: null,
    country: null,
  },
  documents: {
    id_front: null,
    id_back: null,
  },
  selfie: null,
  confirmations: {
    agree_terms: false,
    certify_information: false,
  },
});

const classificationErrors = reactive({
  business_type: "",
  service_category: "",
});
const membershipErrors = reactive({
  package_id: "",
});
const basicInfoErrors = reactive({
  business_name: "",
  business_email: "",
  business_phone: "",
  tax_id: "",
});
const contactErrors = reactive({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  phone_alt: "",
});
const addressErrors = reactive({
  line_1: "",
  city: "",
  state: "",
  zip: "",
});
const documentsErrors = reactive({
  id_front: "",
  id_back: "",
});
const selfieErrors = reactive({
  selfie: "",
});
const reviewErrors = reactive({
  agree_terms: "",
  certify_information: "",
});

const businessTypeOptions = ref<{ label: string; value: string }[]>([]);

const serviceCategoryOptions = ref<{ label: string; value: string }[]>([]);

const membershipPackages = ref<MembershipPackage[]>([]);

const selectedMembershipLabel = computed(() => {
  const id = form.membership.package_id?.trim?.() || "";
  return membershipPackages.value.find((p) => p.id === id)?.label || "";
});

const selectedMembershipPackage = computed(() => {
  const id = form.membership.package_id?.trim?.() || "";
  return membershipPackages.value.find((p) => p.id === id) || null;
});

const membershipAmountDue = computed(() => {
  const pkg: any = selectedMembershipPackage.value;
  if (!pkg) return null;

  const key = form.membership.billing_interval === "yearly" ? "price_yearly" : "price_monthly";
  const raw = pkg?.[key];
  const amount = Number(raw);
  return Number.isFinite(amount) ? amount : null;
});

const companyPublishableKey = computed(() => appStore.company?.publishable_key || "");

const currentStepLabel = computed(() => steps[stepIndex.value].label);
const currentStepPercent = computed(() => steps[stepIndex.value].percent);
const currentStepComponent = computed<Component>(() => {
  if (stepIndex.value === 0) return ClassificationForm;
  if (stepIndex.value === 1) return BasicInfoForm;
  if (stepIndex.value === 2) return ContactPersonForm;
  if (stepIndex.value === 3) return AddressForm;
  if (stepIndex.value === 4) return DocumentsForm;
  if (stepIndex.value === 5) return SelfieForm;
  if (stepIndex.value === 6) return MembershipPackageForm;
  return ReviewForm;
});
const currentStepKey = computed(() => `join-vendor-step-${stepIndex.value}`);

const currentStepProps = computed<Record<string, unknown>>(() => {
  if (stepIndex.value === 0) {
    return {
      business_type: form.basicInfo.business_type,
      service_category: form.basicInfo.service_category,
      businessTypeOptions: businessTypeOptions.value,
      serviceCategoryOptions: serviceCategoryOptions.value,
      errors: classificationErrors,
    };
  }

  if (stepIndex.value === 1) {
    return {
      business_name: form.basicInfo.business_name,
      business_type: form.basicInfo.business_type,
      tax_id: form.basicInfo.tax_id,
      business_email: form.basicInfo.business_email,
      business_phone: form.basicInfo.business_phone,
      errors: basicInfoErrors,
    };
  }

  if (stepIndex.value === 2) {
    return {
      first_name: form.contactPerson.first_name,
      last_name: form.contactPerson.last_name,
      email: form.contactPerson.email,
      phone: form.contactPerson.phone,
      phone_alt: form.contactPerson.phone_alt,
      errors: contactErrors,
    };
  }

  if (stepIndex.value === 3) {
    return {
      line_1: form.address.line_1,
      line_2: form.address.line_2,
      city: form.address.city,
      state: form.address.state,
      zip: form.address.zip,
      lat: form.address.lat,
      lng: form.address.lng,
      country_code: form.address.country_code,
      country: form.address.country,
      errors: addressErrors,
    };
  }

  if (stepIndex.value === 4) {
    return {
      id_front: form.documents.id_front,
      id_back: form.documents.id_back,
      errors: documentsErrors,
    };
  }

  if (stepIndex.value === 5) {
    return {
      selfie: form.selfie,
      errors: selfieErrors,
    };
  }

  if (stepIndex.value === 6) {
    return {
      package_id: form.membership.package_id,
      billing_interval: form.membership.billing_interval,
      errors: membershipErrors,
    };
  }

  return {
    membership_package: selectedMembershipLabel.value,
    membership_package_data: selectedMembershipPackage.value,
    billing_interval: form.membership.billing_interval,
    stripe_publishable_key: stripeConfig.publishableKey,
    stripe_account: stripeConfig.stripeAccount,
    basicInfo: form.basicInfo,
    contactPerson: form.contactPerson,
    address: form.address,
    documents: form.documents,
    selfie: form.selfie,
    agree_terms: form.confirmations.agree_terms,
    certify_information: form.confirmations.certify_information,
    errors: reviewErrors,
  };
});

const currentStepListeners = computed<Record<string, (...args: any[]) => void>>(() => {
  if (stepIndex.value === 0) {
    return {
      "update:business_type": (value: string) => {
        form.basicInfo.business_type = value;
        classificationErrors.business_type = "";
      },
      "update:service_category": (value: string) => {
        form.basicInfo.service_category = value;
        classificationErrors.service_category = "";
      },
    };
  }

  if (stepIndex.value === 1) {
    return {
      "update:business_name": (value: string) => {
        form.basicInfo.business_name = value;
        basicInfoErrors.business_name = "";
      },
      "update:tax_id": (value: string) => {
        form.basicInfo.tax_id = value;
        basicInfoErrors.tax_id = "";
      },
      "update:business_email": (value: string) => {
        form.basicInfo.business_email = value;
        basicInfoErrors.business_email = "";
      },
      "update:business_phone": (value: string) => {
        form.basicInfo.business_phone = value;
        basicInfoErrors.business_phone = "";
      },
    };
  }

  if (stepIndex.value === 2) {
    return {
      "update:first_name": (value: string) => {
        form.contactPerson.first_name = value;
        contactErrors.first_name = "";
      },
      "update:last_name": (value: string) => {
        form.contactPerson.last_name = value;
        contactErrors.last_name = "";
      },
      "update:email": (value: string) => {
        form.contactPerson.email = value;
        contactErrors.email = "";
      },
      "update:phone": (value: string) => {
        form.contactPerson.phone = value;
        contactErrors.phone = "";
      },
      "update:phone_alt": (value: string) => {
        form.contactPerson.phone_alt = value;
        contactErrors.phone_alt = "";
      },
    };
  }

  if (stepIndex.value === 3) {
    return {
      "update:line_1": (value: string) => {
        form.address.line_1 = value;
        addressErrors.line_1 = "";
      },
      "update:line_2": (value: string) => {
        form.address.line_2 = value;
      },
      "update:city": (value: string) => {
        form.address.city = value;
        addressErrors.city = "";
      },
      "update:state": (value: string) => {
        form.address.state = value;
        addressErrors.state = "";
      },
      "update:zip": (value: string) => {
        form.address.zip = value;
        addressErrors.zip = "";
      },
      "update:lat": (value: string | null) => {
        form.address.lat = value;
      },
      "update:lng": (value: string | null) => {
        form.address.lng = value;
      },
      "update:country_code": (value: string | null) => {
        form.address.country_code = value;
      },
      "update:country": (value: string | null) => {
        form.address.country = value;
      },
    };
  }

  if (stepIndex.value === 4) {
    return {
      "update:id_front": (value: File | null) => {
        form.documents.id_front = value;
        documentsErrors.id_front = "";
      },
      "update:id_back": (value: File | null) => {
        form.documents.id_back = value;
        documentsErrors.id_back = "";
      },
    };
  }

  if (stepIndex.value === 5) {
    return {
      "update:selfie": (value: File | null) => {
        form.selfie = value;
        selfieErrors.selfie = "";
      },
    };
  }

  if (stepIndex.value === 6) {
    return {
      "update:package_id": (value: string) => {
        form.membership.package_id = value;
        membershipErrors.package_id = "";
      },
      "update:billing_interval": (value: "monthly" | "yearly") => {
        form.membership.billing_interval = value;
      },
      "packages-loaded": (packages: MembershipPackage[]) => {
        membershipPackages.value = packages;
      },
    };
  }

  return {
    "update:agree_terms": (value: boolean) => {
      form.confirmations.agree_terms = value;
      reviewErrors.agree_terms = "";
    },
    "update:certify_information": (value: boolean) => {
      form.confirmations.certify_information = value;
      reviewErrors.certify_information = "";
    },
    "click:terms": () => {
      modal.open(TermsPrivacyPage, {}, { title: "Terms & Privacy", maxWidth: 720, showHeader: true });
    },
  };
});

const canContinue = computed(() => {
  return validateStep(stepIndex.value, false);
});

function isValidEmail(value: string) {
  return /.+@.+\..+/.test(value);
}

function validateStep(index: number, applyErrors: boolean) {
  if (index === 0) {
    const nextErrors = {
      business_type: form.basicInfo.business_type.trim() ? "" : "Business type is required",
      service_category: form.basicInfo.service_category.trim() ? "" : "Service category is required",
    };

    if (applyErrors) {
      classificationErrors.business_type = nextErrors.business_type;
      classificationErrors.service_category = nextErrors.service_category;
    }

    return !nextErrors.business_type && !nextErrors.service_category;
  }

  if (index === 1) {
    const nextErrors = {
      business_name: form.basicInfo.business_name.trim() ? "" : "Business name is required",
    };

    if (applyErrors) {
      basicInfoErrors.business_name = nextErrors.business_name;
    }

    return !nextErrors.business_name;
  }

  if (index === 2) {
    const emailValue = form.contactPerson.email.trim();
    const nextErrors = {
      first_name: form.contactPerson.first_name.trim() ? "" : "First name is required",
      last_name: form.contactPerson.last_name.trim() ? "" : "Last name is required",
      email: emailValue ? (isValidEmail(emailValue) ? "" : "Enter a valid email") : "Email is required",
      phone: form.contactPerson.phone.trim() ? "" : "Phone is required",

    };

    if (applyErrors) {
      contactErrors.first_name = nextErrors.first_name;
      contactErrors.last_name = nextErrors.last_name;
      contactErrors.email = nextErrors.email;
      contactErrors.phone = nextErrors.phone;
    }

    return !nextErrors.first_name && !nextErrors.last_name && !nextErrors.email && !nextErrors.phone;
  }

  if (index === 3) {
    const nextErrors = {
      line_1: form.address.line_1.trim() ? "" : "Address line 1 is required",
      city: form.address.city.trim() ? "" : "City is required",
      state: form.address.state.trim() ? "" : "State is required",
      zip: form.address.zip.trim() ? "" : "ZIP is required",
    };

    if (applyErrors) {
      addressErrors.line_1 = nextErrors.line_1;
      addressErrors.city = nextErrors.city;
      addressErrors.state = nextErrors.state;
      addressErrors.zip = nextErrors.zip;
    }

    return !nextErrors.line_1 && !nextErrors.city && !nextErrors.state && !nextErrors.zip;
  }

  if (index === 4) {
    const nextErrors = {
      id_front: form.documents.id_front ? "" : "ID front is required",
      id_back: form.documents.id_back ? "" : "ID back is required",
    };

    if (applyErrors) {
      documentsErrors.id_front = nextErrors.id_front;
      documentsErrors.id_back = nextErrors.id_back;
    }

    return !nextErrors.id_front && !nextErrors.id_back;
  }

  if (index === 5) {
    const nextErrors = {
      selfie: form.selfie ? "" : "Selfie is required",
    };

    if (applyErrors) {
      selfieErrors.selfie = nextErrors.selfie;
    }

    return !nextErrors.selfie;
  }

  if (index === 6) {
    const nextErrors = {
      package_id: form.membership.package_id.trim() ? "" : "Membership package is required",
    };

    if (applyErrors) {
      membershipErrors.package_id = nextErrors.package_id;
    }

    return !nextErrors.package_id;
  }

  const nextErrors = {
    agree_terms: form.confirmations.agree_terms ? "" : "You must agree to the terms",
    certify_information: form.confirmations.certify_information ? "" : "You must certify the information",
  };

  if (applyErrors) {
    reviewErrors.agree_terms = nextErrors.agree_terms;
    reviewErrors.certify_information = nextErrors.certify_information;
  }

  return !nextErrors.agree_terms && !nextErrors.certify_information;
}

function handlePrevious() {
  stepTransitionName.value = "step-back";
  if (stepIndex.value > 0) {
    stepIndex.value -= 1;
  }
}

function handleNext() {
  stepTransitionName.value = "step-forward";
  if (!validateStep(stepIndex.value, true)) {
    return;
  }
  if (stepIndex.value < steps.length - 1) {
    stepIndex.value += 1;
  }
}

async function handleSubmit() {
  if (isSubmitting.value || isSubmitted.value) return;
  if (!validateStep(stepIndex.value, true)) return;

  const idFront = form.documents.id_front;
  const idBack = form.documents.id_back;
  const selfie = form.selfie;

  if (!idFront || !idBack || !selfie) {
    return;
  }

  try {
    isSubmitting.value = true;

    // First, create vendor request with all form data
    const formData = new FormData();
    formData.append("basic_info[business_name]", form.basicInfo.business_name.trim());
    formData.append("basic_info[business_type]", form.basicInfo.business_type.trim());
    formData.append("basic_info[service_category]", form.basicInfo.service_category.trim());
    formData.append("basic_info[tax_id]", form.basicInfo.tax_id.trim());
    formData.append("basic_info[business_email]", form.basicInfo.business_email.trim());
    formData.append("basic_info[business_phone]", form.basicInfo.business_phone.trim());

    formData.append("membership[company_plan_id]", form.membership.package_id.trim());
    formData.append("membership[billing_interval]", form.membership.billing_interval);

    formData.append("contact[first_name]", form.contactPerson.first_name.trim());
    formData.append("contact[last_name]", form.contactPerson.last_name.trim());
    formData.append("contact[email]", form.contactPerson.email.trim());
    formData.append("contact[phone]", form.contactPerson.phone.trim());
    formData.append("contact[phone_alt]", form.contactPerson.phone_alt.trim());

    formData.append("address[line_1]", form.address.line_1.trim());
    formData.append("address[line_2]", form.address.line_2.trim());
    formData.append("address[city]", form.address.city.trim());
    formData.append("address[state]", form.address.state.trim());
    formData.append("address[zip]", form.address.zip.trim());
    formData.append("address[lat]", form.address.lat || "");
    formData.append("address[lng]", form.address.lng || "");
    formData.append("address[country_code]", form.address.country_code || "");
    formData.append("address[country]", form.address.country || "");

    formData.append("documents[id_front]", idFront);
    formData.append("documents[id_back]", idBack);
    formData.append("documents[selfie]", selfie);

    const vendorResponse = await createVendorRequest(formData);
    const vendorId = vendorResponse?.vendor_id || vendorResponse?.data?.vendor_id;

    if (!vendorId) {
      throw new Error("Failed to create vendor request.");
    }

    // Now create payment intent for the vendor membership
    const payload = {
        company_vendor_id: vendorResponse?.company_vendor_id || vendorResponse?.data?.company_vendor_id,
        company_plan_id: form.membership.package_id.trim(),
        billing_interval: form.membership.billing_interval,
    };

    const intentResponse = await createVendorMembershipIntent(payload);
    console.log(intentResponse, "Received intent response from server");
    // Align parsing with marketplace flows: support a few common response shapes.
    const intentPayload =
      intentResponse?.original?.data ||
      intentResponse?.data ||
      intentResponse;

    const clientSecret =
      intentPayload?.clientSecret ||
      intentPayload?.client_secret ||
      intentResponse?.clientSecret ||
      intentResponse?.client_secret ||
      intentResponse?.data?.clientSecret ||
      intentResponse?.data?.client_secret;

    const intentType =
      intentPayload?.intentType ||
      intentPayload?.intent_type ||
      intentPayload?.intentType?.replace?.("-", "_") ||
      intentPayload?.intent_type?.replace?.("-", "_") ||
      intentResponse?.data?.intentType ||
      intentResponse?.data?.intent_type ||
      "payment";

    const membershipId =
      intentPayload?.membershipId ||
      intentPayload?.membership_id ||
      intentResponse?.data?.membershipId ||
      intentResponse?.data?.membership_id;

    const intentId =
      intentPayload?.intentId ||
      intentPayload?.intent_id ||
      intentPayload?.stripeIntentId ||
      intentPayload?.stripe_intent_id ||
      intentResponse?.data?.intentId ||
      intentResponse?.data?.intent_id ||
      intentResponse?.data?.stripeIntentId ||
      intentResponse?.data?.stripe_intent_id;

    const stripeIntentId =
      intentPayload?.stripeIntentId ||
      intentPayload?.stripe_intent_id ||
      intentResponse?.stripeIntentId ||
      intentResponse?.stripe_intent_id ||
      intentResponse?.data?.stripeIntentId ||
      intentResponse?.data?.stripe_intent_id ||
      intentId;

    // If the backend created the intent on a connected Stripe account, Stripe.js must be
    // initialized with the same account context (and sometimes a different publishable key).
    stripeConfig.publishableKey =
      intentPayload?.publishableKey ||
      intentPayload?.publishable_key ||
      intentPayload?.stripePublishableKey ||
      intentPayload?.stripe_publishable_key ||
      stripeConfig.publishableKey ||
      "";
    stripeConfig.stripeAccount =
      intentPayload?.stripeAccount ||
      intentPayload?.stripe_account ||
      intentPayload?.stripe_account_id ||
      intentPayload?.stripeAccountId ||
      intentPayload?.stripe_accountID ||
      intentPayload?.account ||
      intentPayload?.account_id ||
      intentPayload?.connect_account ||
      intentPayload?.connect_account_id ||
      intentPayload?.stripe_connect_account ||
      intentPayload?.stripe_connect_account_id ||
      intentPayload?.connectedAccountId ||
      intentPayload?.connected_account_id ||
      stripeConfig.stripeAccount ||
      "";

    if (process.env.NODE_ENV !== "production") {
      const pk = stripeConfig.publishableKey || companyPublishableKey.value || "";
      const pkMode = pk.startsWith("pk_live_") ? "live" : pk.startsWith("pk_test_") ? "test" : "unknown";
      console.info("[vendor-membership] stripe intent context", {
        intentType,
        intentId,
        stripeIntentId,
        membershipId,
        stripeAccount: stripeConfig.stripeAccount || null,
        publishableKeyMode: pkMode,
      });
      if (!stripeConfig.stripeAccount) {
        console.warn(
          "[vendor-membership] No connected account id in intent response. If the PaymentIntent was created on a connected account, Stripe confirm will 404 unless the frontend initializes Stripe with { stripeAccount }.",
        );
      }
    }
    
    // Intentionally do not log `clientSecret` (sensitive).

    const responseSavedPaymentMethodId =
      intentPayload?.savedPaymentMethodId ||
      intentPayload?.saved_payment_method_id ||
      intentResponse?.savedPaymentMethodId ||
      intentResponse?.saved_payment_method_id ||
      intentResponse?.data?.savedPaymentMethodId ||
      intentResponse?.data?.saved_payment_method_id;

    if (!clientSecret && !responseSavedPaymentMethodId) {
      throw new Error("Missing client secret from payment response.");
    }
    if (clientSecret && (typeof clientSecret !== "string" || !clientSecret.includes("_secret_"))) {
      // Stripe confirmCardPayment expects a client secret, not a PaymentIntent id.
      throw new Error("Invalid Stripe client secret returned from server.");
    }

    if (currentStepComponent.value !== ReviewForm) throw new Error("Payment step is not active.");


    const billingDetails = {
      name: `${form.contactPerson.first_name} ${form.contactPerson.last_name}`.trim(),
      email: form.contactPerson.email,
      phone: form.contactPerson.phone,
      address: {
        line1: form.address.line_1,
        line2: form.address.line_2,
        city: form.address.city,
        state: form.address.state,
        postal_code: form.address.zip,
      },
    };

    // Saved card path: backend already has payment method, no Stripe element.
    if (responseSavedPaymentMethodId) {
      const chargeResult = await chargeSavedVendorMembershipCard({
        membership_id: membershipId,
        vendor_id: vendorId,
        payment_method_id: responseSavedPaymentMethodId,
        intent_id: intentId,
      });

      const chargedStripeIntentId =
        chargeResult?.payment_intent_id ||
        chargeResult?.setup_intent_id ||
        chargeResult?.intent_id ||
        stripeIntentId;

      await finalizeVendorMembership({
        membership_id: membershipId,
        vendor_id: vendorId,
        payment_method_id: chargeResult?.payment_method_id || responseSavedPaymentMethodId,
        payment_intent_id: chargeResult?.payment_intent_id,
        setup_intent_id: chargeResult?.setup_intent_id,
        stripe_intent_id: chargedStripeIntentId,
        intent_type: intentType,
        intent_id: intentId,
      });

      console.log("vendor membership charged via saved card", chargeResult);
      isSubmitted.value = true;
      return;
    }

    if (!paymentBlockRef.value?.confirmPayment) {
      throw new Error("Payment form is not ready.");
    }

    const confirmation =
      intentType === "setup" || intentType === "setup_intent"
        ? await paymentBlockRef.value.confirmSetup(clientSecret, billingDetails)
        : await paymentBlockRef.value.confirmPayment(clientSecret, billingDetails);

    // Normalize/unwrap conditional fields so TypeScript can safely access them
    const paymentIntentId = (confirmation as any).paymentIntentId;
    const setupIntentId = (confirmation as any).setupIntentId;
    const paymentMethodId = (confirmation as any).paymentMethodId;

    // Finalize membership with payment details
    await finalizeVendorMembership({
      membership_id: membershipId,
      vendor_id: vendorId,
      payment_intent_id: paymentIntentId,
      setup_intent_id: setupIntentId,
      payment_method_id: paymentMethodId,
      stripe_intent_id: paymentIntentId || setupIntentId || stripeIntentId,
      intent_type: intentType,
      intent_id: intentId,
    });

    console.log("Vendor membership payment and request completed successfully");
    isSubmitted.value = true;
  } catch (error) {
    console.error("Failed to submit vendor membership request", error);
  } finally {
    isSubmitting.value = false;
  }
}
onMounted(async () => {
  try {
    const response = await fetchServiceGroups();
    
    // Populate service category options
    serviceCategoryOptions.value = response.data.map((group: any) => ({
      label: group.label,
      value: group.slug,
    }));

    // TODO: Add fetch for business types when API is available
    // For now, set default business type options
    businessTypeOptions.value = [
      { label: "Sole Proprietorship", value: "sole_proprietorship" },
      { label: "Partnership", value: "partnership" },
      { label: "LLC", value: "llc" },
      { label: "Corporation", value: "corporation" },
      { label: "Freelancer", value: "freelancer" },
    ];
  } catch (error) {
    console.error('Failed to fetch service groups:', error);
    
    // Fallback to default options
    serviceCategoryOptions.value = [
      { label: "Pest Control", value: "pest_control" },
      { label: "Cleaning", value: "cleaning" },
      { label: "Plumbing", value: "plumbing" },
      { label: "Electrical", value: "electrical" },
      { label: "HVAC", value: "hvac" },
      { label: "Landscaping", value: "landscaping" },
    ];
    
    businessTypeOptions.value = [
      { label: "Sole Proprietorship", value: "sole_proprietorship" },
      { label: "Partnership", value: "partnership" },
      { label: "LLC", value: "llc" },
      { label: "Corporation", value: "corporation" },
      { label: "Freelancer", value: "freelancer" },
    ];
  }
});
</script>

<style scoped>
.form-content {
}

.bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 16px 24px 16px;
  border-top: 1px solid var(--border-color);
  background: var(--color-white);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.bottom-btn {
  text-transform: none;
}

.success-wrap {
  min-height: calc(100vh - 220px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 20px;
}

.success-title {
  margin: 0;
  color: #1f2430;
  font-size: 26px;
  font-weight: 700;
}

.success-subtitle {
  margin: 0;
  color: #667085;
  max-width: 520px;
}

.step-forward-enter-active,
.step-forward-leave-active,
.step-back-enter-active,
.step-back-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.step-forward-enter-from,
.step-back-leave-to {
  opacity: 0;
  transform: translateX(18px);
}

.step-forward-leave-to,
.step-back-enter-from {
  opacity: 0;
  transform: translateX(-18px);
}

</style>
